// REACT IMPORTS
import React, { useContext, useMemo, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	IconButton,
	MenuItem,
	Select,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReplayIcon from "@mui/icons-material/Replay";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import { SortableHeaderCell, useSortedRows } from "./sortableTable";

// CONSTANTS
import { fmtHours } from "../../Projects/ProjectViewer/BudgetEditorTab";
import {
	DAY_GROUPS,
	DAY_STATUS,
	REQUEST_STATUS,
	REQUEST_SYNC,
	canRetryDay,
	inSyncWindow,
	SYNC_LOOKBACK_DAYS,
	fmtDate,
	fmtDateTime,
	fmtDay,
	titleCase,
} from "./timeOffConstants";

const EMPTY_FILTERS = {
	employee: null,
	type: "",
	status: "",
	daySync: "",
	from: "",
	to: "",
};
const REQUEST_COLUMNS = 9;
const DAY_COLUMNS = 9;

// A request's hours: BambooHR's total, else the sum of its current days
// ('deleted' days were dropped from the request)
const requestHours = (r) =>
	r.amount_unit === "hours" && r.total_amount != null
		? r.total_amount
		: r.days
				.filter((d) => d.sync_status !== "deleted")
				.reduce((sum, d) => sum + Number(d.requested_hours || 0), 0);
// Sort values per column. BQE Sync sorts by attention (errors first), in
// REQUEST_SYNC's order.
const SYNC_RANK = Object.fromEntries(
	Object.keys(REQUEST_SYNC).map((key, i) => [key, i]),
);
const SORT_GETTERS = {
	employee: (r) => r.employee_name,
	type: (r) => r.type_name,
	status: (r) => r.status,
	start: (r) => r.start_date,
	end: (r) => r.end_date,
	hours: (r) => Number(requestHours(r)),
	sync: (r) => SYNC_RANK[r.sync_status] ?? Object.keys(SYNC_RANK).length,
	lastSynced: (r) => r.last_synced,
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Shared style for the small status pills
const pillStyle = (meta) => ({
	display: "inline-flex",
	alignItems: "center",
	height: 20,
	padding: "0 9px",
	borderRadius: 999,
	fontSize: 11,
	fontWeight: 700,
	textTransform: "uppercase",
	letterSpacing: "0.03em",
	whiteSpace: "nowrap",
	background: meta.bg,
	color: meta.fg,
});

const DetailField = ({ label, children, grow }) => (
	<div
		className={`timeOffConsoleDetailField${grow ? " timeOffConsoleDetailFieldGrow" : ""}`}
	>
		<span className="timeOffConsoleSectionLabel">{label}</span>
		<span>{children}</span>
	</div>
);

// One day of a request: sync status picker and retry action
const DayRow = ({ day, canRetry, saving, onRetry, onStatusChange }) => {
	const meta = DAY_STATUS[day.sync_status] || DAY_STATUS.pending;

	return (
		<TableRow
			style={{
				background: day.sync_status === "error" ? meta.bg : undefined,
			}}
		>
			<TableCell className="timeOffConsoleNoWrap">
				{fmtDay(day.off_date)}
			</TableCell>
			<TableCell align="right">{fmtHours(day.requested_hours)}</TableCell>
			<TableCell align="right">
				{day.bqe_hours == null ? "—" : fmtHours(day.bqe_hours)}
			</TableCell>
			<TableCell>
				<Select
					size="small"
					variant="standard"
					disableUnderline
					value={day.sync_status}
					disabled={saving}
					onChange={(e) => onStatusChange(e.target.value)}
					className="timeOffConsoleStatusSelect"
					style={{ color: meta.fg, background: meta.bg }}
				>
					{Object.entries(DAY_STATUS).map(([key, m]) => (
						<MenuItem key={key} value={key}>
							{m.label}
						</MenuItem>
					))}
				</Select>
			</TableCell>
			<TableCell align="right">{day.attempts}</TableCell>
			<TableCell className="timeOffConsoleMono timeOffConsoleNoWrap">
				{fmtDateTime(day.synced)}
			</TableCell>
			<TableCell className="timeOffConsoleMono">
				{day.bqe_time_entry_id ? (
					<Tooltip
						title={day.bqe_time_entry_id}
						arrow
						placement="top"
					>
						<span>{day.bqe_time_entry_id.slice(0, 8)}</span>
					</Tooltip>
				) : (
					"—"
				)}
			</TableCell>
			<TableCell className="timeOffConsoleDayError">
				{day.last_error ? (
					<Tooltip title={day.last_error} arrow placement="top">
						<span>{day.last_error}</span>
					</Tooltip>
				) : (
					"—"
				)}
			</TableCell>
			<TableCell align="right" className="timeOffConsoleNoWrap">
				{saving ? (
					<CircularProgress size={16} />
				) : (
					canRetry && (
						<Tooltip
							title="Retry on the next sync run"
							arrow
							placement="top"
						>
							<IconButton size="small" onClick={onRetry}>
								<ReplayIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)
				)}
			</TableCell>
		</TableRow>
	);
};

// REACT COMPONENT
const TimeOffRequestsTab = ({
	data,
	types,
	isLoading,
	onChanged,
	onFromFilterChange,
}) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATE
	const [filters, setFilters] = useState(EMPTY_FILTERS);
	const [groupFilter, setGroupFilter] = useState("");
	const [expanded, setExpanded] = useState({});
	const [savingIds, setSavingIds] = useState({});
	const [snack, setSnack] = useState(null);

	const requests = useMemo(() => data?.requests || [], [data]);

	// DERIVED DATA
	const employeeOptions = useMemo(
		() =>
			[
				...new Set(
					requests.map((r) => r.employee_name).filter(Boolean),
				),
			].sort(),
		[requests],
	);
	const statusOptions = useMemo(
		() =>
			[
				...new Set(requests.map((r) => String(r.status).toLowerCase())),
			].sort(),
		[requests],
	);
	const typeOptions = useMemo(() => {
		const byId = new Map(types.map((t) => [t.bamboohr_type_id, t.name]));
		requests.forEach(
			(r) =>
				byId.has(r.bamboohr_type_id) ||
				byId.set(r.bamboohr_type_id, r.type_name),
		);
		return [...byId.entries()]
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [types, requests]);

	const groupCounts = useMemo(() => {
		const counts = Object.fromEntries(DAY_GROUPS.map((g) => [g.key, 0]));
		requests.forEach((r) =>
			r.days.forEach((d) => {
				const group = DAY_STATUS[d.sync_status]?.group;
				if (group) counts[group] += 1;
			}),
		);
		return counts;
	}, [requests]);

	const filtered = useMemo(
		() =>
			requests.filter(
				(r) =>
					(!filters.employee ||
						r.employee_name === filters.employee) &&
					(!filters.type || r.bamboohr_type_id === filters.type) &&
					(!filters.status ||
						String(r.status).toLowerCase() === filters.status) &&
					(!filters.daySync ||
						r.days.some(
							(d) => d.sync_status === filters.daySync,
						)) &&
					(!filters.from || r.end_date >= filters.from) &&
					(!filters.to || r.start_date <= filters.to) &&
					(!groupFilter ||
						r.days.some(
							(d) =>
								DAY_STATUS[d.sync_status]?.group ===
								groupFilter,
						)),
			),
		[requests, filters, groupFilter],
	);
	const { sorted, sortKey, sortDir, onSort } = useSortedRows(
		filtered,
		SORT_GETTERS,
		"start",
		"desc",
		["start", "end", "hours", "lastSynced"],
	);
	const sortProps = { activeKey: sortKey, dir: sortDir, onSort };

	// INPUT HANDLERS
	const setFilter = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		if (key === "from") onFromFilterChange(value);
		if (key === "daySync") setGroupFilter("");
	};

	// The summary cards and the Day Sync Status filter are one filter: picking
	// either clears the other
	const toggleGroupFilter = (key) => {
		setGroupFilter((prev) => (prev === key ? "" : key));
		setFilters((prev) => ({ ...prev, daySync: "" }));
	};
	const activeGroup = groupFilter || DAY_STATUS[filters.daySync]?.group || "";

	const clearFilters = () => {
		setFilters(EMPTY_FILTERS);
		setGroupFilter("");
	};

	const toggleExpanded = (id) =>
		setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

	const markSaving = (ids, saving) =>
		setSavingIds((prev) => {
			const next = { ...prev };
			ids.forEach((id) => (saving ? (next[id] = true) : delete next[id]));
			return next;
		});

	// API HELPER FUNCTIONS
	const updateDays = async (endpoint, ids, successText, body = {}) => {
		markSaving(ids, true);
		try {
			await postAPICall(instance, accounts[0], endpoint, {
				...body,
				ids,
				modifiedBy: employeeID,
			});
			await onChanged();
			setSnack({ severity: "success", text: successText });
		} catch (err) {
			console.error(err);
			setSnack({
				severity: "error",
				text: err?.response?.data?.error || "Couldn't save the change.",
			});
		}
		markSaving(ids, false);
	};

	const retryDays = (ids) =>
		updateDays(
			"/api/time-off/days/retry",
			ids,
			`${plural(ids.length, "day")} queued for the next sync run.`,
		);

	const setDayStatus = (day, status) =>
		updateDays(
			"/api/time-off/days/status",
			[day.id],
			status === "pending"
				? `${fmtDay(day.off_date)} queued for the next sync run.`
				: `${fmtDay(day.off_date)} set to ${DAY_STATUS[status].label}.`,
			{ sync_status: status },
		);

	// RENDER
	if (!data && isLoading) {
		return (
			<div className="loadingDiv">
				<CircularProgress color="primary" />
			</div>
		);
	}

	const renderExpanded = (r) => {
		const syncable = inSyncWindow(r);
		const failedDays = syncable ? r.days.filter(canRetryDay) : [];
		const staleFailures = !syncable && r.days.some(canRetryDay);
		const rowSaving = r.days.some((d) => savingIds[d.id]);

		return (
			<div className="timeOffConsoleExpandedPanel">
				<div className="timeOffConsoleDetailFields">
					<DetailField label="Requested">
						{fmtDate(r.bamboohr_created)}
					</DetailField>
					<DetailField label="Amount">
						{r.total_amount != null
							? `${fmtHours(r.total_amount)} ${r.amount_unit || ""}`
							: "—"}
					</DetailField>
					<DetailField label="Type Sync">
						{r.type_id == null
							? "Type not mapped"
							: !r.type_is_synced
								? "Off for this type"
								: r.bqe_activity_id && r.bqe_project_id
									? "On"
									: "No BQE activity or project"}
					</DetailField>
					<DetailField label="Employee Note" grow>
						{r.employee_note || "—"}
					</DetailField>
					{r.manager_note && (
						<DetailField label="Manager Note" grow>
							{r.manager_note}
						</DetailField>
					)}
				</div>

				{r.last_error && (
					<pre className="timeOffConsoleErrorBox">{r.last_error}</pre>
				)}

				<div className="timeOffConsoleDaysBox">
					<Table size="small" className="timeOffConsoleDaysTable">
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell align="right">Requested</TableCell>
								<TableCell align="right">BQE Hours</TableCell>
								<TableCell>Sync Status</TableCell>
								<TableCell align="right">Attempts</TableCell>
								<TableCell>Synced At</TableCell>
								<TableCell>BQE Entry</TableCell>
								<TableCell>Error</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{r.days.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={DAY_COLUMNS}
										className="timeOffConsoleMuted"
									>
										No days recorded for this request yet.
									</TableCell>
								</TableRow>
							)}
							{r.days.map((d) => (
								<DayRow
									key={d.id}
									day={d}
									canRetry={syncable && canRetryDay(d)}
									saving={Boolean(savingIds[d.id])}
									onRetry={() => retryDays([d.id])}
									onStatusChange={(status) =>
										setDayStatus(d, status)
									}
								/>
							))}
						</TableBody>
					</Table>
				</div>

				<div className="timeOffConsoleExpandedActions">
					<span className="timeOffConsoleMuted">
						{staleFailures
							? `This request ended more than ${SYNC_LOOKBACK_DAYS} days ago, so the sync won't retry it. Fix these days in BQE by hand, then Ignore them.`
							: "Set a day to Pending to retry it on the next sync run, or Ignored to have the sync leave it alone. Hours come from BambooHR."}
					</span>
					{failedDays.length > 0 && (
						<Button
							size="small"
							variant="outlined"
							disabled={rowSaving}
							startIcon={<ReplayIcon />}
							onClick={() =>
								retryDays(failedDays.map((d) => d.id))
							}
						>
							Retry {plural(failedDays.length, "Day")}
						</Button>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="timeOffConsoleBody">
			{/* Summary strip */}
			<div className="timeOffConsoleSummaryStrip">
				{DAY_GROUPS.map((g) => {
					const Icon = g.icon;
					const isMuted = activeGroup && activeGroup !== g.key;
					return (
						<button
							key={g.key}
							type="button"
							className="timeOffConsoleSummaryCard"
							onClick={() => toggleGroupFilter(g.key)}
							style={{
								borderColor: g.fg,
								background: isMuted ? "#fff" : g.bg,
							}}
						>
							<Icon fontSize="small" style={{ color: g.fg }} />
							<span
								className="timeOffConsoleSummaryCount"
								style={{ color: g.fg }}
							>
								{groupCounts[g.key]}
							</span>
							<span
								className="timeOffConsoleSummaryLabel"
								style={{ color: g.fg }}
							>
								{g.label}
							</span>
						</button>
					);
				})}
			</div>

			{/* Toolbar */}
			<div className="timeOffConsoleToolbar">
				<Autocomplete
					size="small"
					sx={{ width: 220 }}
					options={employeeOptions}
					value={filters.employee}
					onChange={(_, v) => setFilter("employee", v)}
					renderInput={(params) => (
						<TextField {...params} label="Employee" />
					)}
				/>
				<TextField
					select
					size="small"
					label="Type"
					sx={{ width: 170 }}
					value={filters.type}
					onChange={(e) => setFilter("type", e.target.value)}
				>
					<MenuItem value="">All Types</MenuItem>
					{typeOptions.map((t) => (
						<MenuItem key={t.id} value={t.id}>
							{t.name}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="BambooHR Status"
					sx={{ width: 200 }}
					value={filters.status}
					onChange={(e) => setFilter("status", e.target.value)}
				>
					<MenuItem value="">All Statuses</MenuItem>
					{statusOptions.map((s) => (
						<MenuItem key={s} value={s}>
							{titleCase(s)}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Day Sync Status"
					sx={{ width: 210 }}
					value={filters.daySync}
					onChange={(e) => setFilter("daySync", e.target.value)}
				>
					<MenuItem value="">All Sync Statuses</MenuItem>
					{Object.entries(DAY_STATUS).map(([key, meta]) => (
						<MenuItem key={key} value={key}>
							{meta.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					type="date"
					label="Start Date"
					sx={{ width: 160 }}
					InputLabelProps={{ shrink: true }}
					value={filters.from}
					onChange={(e) => setFilter("from", e.target.value)}
				/>
				<TextField
					size="small"
					type="date"
					label="End Date"
					sx={{ width: 160 }}
					InputLabelProps={{ shrink: true }}
					value={filters.to}
					onChange={(e) => setFilter("to", e.target.value)}
				/>
				<Button size="small" onClick={clearFilters}>
					Clear
				</Button>
				<div className="timeOffConsoleToolbarSpacer" />
				<span className="timeOffConsoleResultText">
					{filtered.length} of {requests.length} requests
					{data?.from && !filters.from
						? ` · ending since ${fmtDate(data.from)}`
						: ""}
				</span>
			</div>

			{/* Table */}
			<TableContainer className="timeOffConsoleTableContainer">
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<SortableHeaderCell
								sortKey="employee"
								{...sortProps}
							>
								Employee
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="type" {...sortProps}>
								Type
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="status" {...sortProps}>
								Status
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="start" {...sortProps}>
								Start
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="end" {...sortProps}>
								End
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="hours"
								align="right"
								{...sortProps}
							>
								Hours
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="sync" {...sortProps}>
								BQE Sync
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="lastSynced"
								{...sortProps}
							>
								Last Synced
							</SortableHeaderCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{filtered.length === 0 && (
							<TableRow>
								<TableCell colSpan={REQUEST_COLUMNS}>
									<J2NoResultsFound />
								</TableCell>
							</TableRow>
						)}
						{sorted.map((r) => {
							const open = Boolean(expanded[r.id]);
							const sync =
								REQUEST_SYNC[r.sync_status] ||
								REQUEST_SYNC.pending;
							const status =
								REQUEST_STATUS[
									String(r.status).toLowerCase()
								] || REQUEST_STATUS.canceled;
							const hours = requestHours(r);
							const inBqe = r.days.filter(
								(d) => d.sync_status === "created",
							).length;

							return (
								<React.Fragment key={r.id}>
									<TableRow
										hover
										onClick={() => toggleExpanded(r.id)}
										className="timeOffConsoleRow"
										style={{
											background: open
												? "#f5f8fd"
												: r.sync_status === "error"
													? REQUEST_SYNC.error.bg
													: undefined,
										}}
									>
										<TableCell>
											<div className="timeOffConsoleEmployee">
												{r.employee_name || "—"}
											</div>
											<div className="timeOffConsoleMono">
												BambooHR #
												{r.bamboohr_request_id}
											</div>
										</TableCell>
										<TableCell>{r.type_name}</TableCell>
										<TableCell>
											<span style={pillStyle(status)}>
												{r.status}
											</span>
										</TableCell>
										<TableCell className="timeOffConsoleNoWrap">
											{fmtDate(r.start_date)}
										</TableCell>
										<TableCell className="timeOffConsoleNoWrap">
											{fmtDate(r.end_date)}
										</TableCell>
										<TableCell
											align="right"
											style={{ fontWeight: 600 }}
										>
											{fmtHours(hours)}
										</TableCell>
										<TableCell>
											<span className="timeOffConsoleStatusCell">
												<span
													className="timeOffConsoleStatusDot"
													style={{
														background: sync.fg,
													}}
												/>
												<span
													style={{
														color: sync.fg,
														fontWeight: 500,
														fontSize: 12,
													}}
												>
													{sync.label}
												</span>
												{r.days.length > 0 && (
													<span className="timeOffConsoleMuted">
														{inBqe}/{r.days.length}{" "}
														in BQE
													</span>
												)}
											</span>
										</TableCell>
										<TableCell className="timeOffConsoleMono timeOffConsoleNoWrap">
											{fmtDateTime(r.last_synced)}
										</TableCell>
										<TableCell>
											<IconButton size="small">
												{open ? (
													<KeyboardArrowUpIcon />
												) : (
													<KeyboardArrowDownIcon />
												)}
											</IconButton>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											colSpan={REQUEST_COLUMNS}
											// No padding so the panel's background spans the row;
											// no border while closed so rows don't get a double line
											style={{
												padding: 0,
												borderBottom: open
													? undefined
													: "none",
											}}
										>
											<Collapse
												in={open}
												timeout="auto"
												unmountOnExit
											>
												{renderExpanded(r)}
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Snackbar
				open={Boolean(snack)}
				autoHideDuration={4000}
				onClose={() => setSnack(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				{snack ? (
					<Alert
						severity={snack.severity}
						onClose={() => setSnack(null)}
						variant="filled"
					>
						{snack.text}
					</Alert>
				) : (
					<span />
				)}
			</Snackbar>
		</div>
	);
};

export default TimeOffRequestsTab;
